import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Handle, Position } from 'reactflow';
import { X, Loader2, Camera, AlertCircle, ChevronLeft, ChevronRight, Clock3 } from 'lucide-react';
import { toast } from 'sonner';
import { useWorkflowStore } from '../../store/workflowStore';
import { captureFrame, CAPTURE_FRAME_ENABLED } from '../../utils/captureFrame';

const FRAME_STEP = 1 / 30;   // paso "cuadro a cuadro" (fps estimado estándar)
const MAX_SECONDS = 60 * 60;

const fmt = (seconds) => {
  const s = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(s / 60);
  const rest = (s - m * 60).toFixed(2).padStart(5, '0');
  return `${String(m).padStart(2, '0')}:${rest}`;
};

/**
 * Capturar Frame — captura un cuadro exacto del video conectado, en el
 * NAVEGADOR (video → canvas → PNG) y lo sube como asset. No se ejecuta en
 * el servidor ni gasta créditos.
 */
const CaptureFrameNode = memo(({ id, data, selected }) => {
  const updateNodeData = useWorkflowStore((s) => s.updateNodeData);
  const deleteNode = useWorkflowStore((s) => s.deleteNode);
  const getInputForNode = useWorkflowStore((s) => s.getInputForNode);
  const addAsset = useWorkflowStore((s) => s.addAsset);
  const edges = useWorkflowStore((s) => s.edges);

  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState(null);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);
  const videoRef = useRef(null);

  const hasInput = edges.some((e) => e.target === id);
  const inputData = getInputForNode(id);
  const videoUrl = inputData?.outputUrl || null;

  const timeSeconds = Number(data.timeSeconds) || 0;

  // "Desactualizado": la captura guardada ya no refleja el video o el segundo
  // actual. No se borra nada: sólo se marca hasta la próxima captura.
  const stale = Boolean(
    data.outputUrl && (
      (data.sourceVideoUrl && videoUrl && data.sourceVideoUrl !== videoUrl) ||
      (Number.isFinite(Number(data.capturedTimeSeconds)) &&
        Number(data.capturedTimeSeconds) !== timeSeconds)
    )
  );

  useEffect(() => {
    setReady(false);
    setDuration(0);
    setError(null);
  }, [videoUrl]);

  const setTime = useCallback((value) => {
    const next = Math.min(Math.max(0, Number(value) || 0), MAX_SECONDS);
    updateNodeData(id, { timeSeconds: next });
    if (videoRef.current && Number.isFinite(videoRef.current.duration)) {
      videoRef.current.currentTime = Math.min(next, videoRef.current.duration);
    }
  }, [id, updateNodeData]);

  const stepFrame = useCallback((direction) => {
    setTime(timeSeconds + direction * FRAME_STEP);
  }, [setTime, timeSeconds]);

  const handleCapture = useCallback(async () => {
    if (!CAPTURE_FRAME_ENABLED || capturing) return;
    setCapturing(true);
    setError(null);
    try {
      const result = await captureFrame(id, timeSeconds);
      addAsset({
        id: result.assetId || `asset_${Date.now()}`,
        type: 'image',
        tool: 'captureFrame',
        filename: `capture_${timeSeconds.toFixed(2)}s.png`,
        url: result.outputUrl,
        createdAt: new Date().toISOString(),
      });
      toast.success(
        result.clamped
          ? `El video dura menos: capturé el último cuadro (${fmt(result.timeSeconds)})`
          : `Cuadro capturado: ${result.width}×${result.height} · ${fmt(result.timeSeconds)}`,
        { position: 'bottom-right' },
      );
    } catch (err) {
      const message = err?.message || 'No se pudo capturar el cuadro';
      setError(message);
      toast.error(message, { position: 'bottom-right' });
    } finally {
      setCapturing(false);
    }
  }, [id, timeSeconds, capturing, addAsset]);

  const statusLabel = useMemo(() => {
    if (error) return 'error';
    if (capturing) return 'capturando';
    if (!hasInput) return 'sin video';
    if (!ready) return 'cargando';
    if (stale) return 'desactualizado';
    if (data.outputUrl) return 'capturado';
    return 'listo';
  }, [error, capturing, hasInput, ready, stale, data.outputUrl]);

  return (
    <div className={`captureFrame-node node-card ${selected ? 'selected' : ''}`}
         data-testid={`capture-frame-node-${id}`}>
      <Handle type="target" position={Position.Left} id="input"
              className="node-handle node-handle-video" />
      <Handle type="source" position={Position.Right} id="output"
              className="node-handle node-handle-image" />

      <div className="node-header captureFrame-header">
        <div className="node-header-left">
          <Camera size={14} />
          <span>Capturar Frame</span>
          <span className={`captureFrame-status captureFrame-status-${statusLabel}`}
                data-testid={`capture-frame-status-${id}`}>
            {statusLabel}
          </span>
        </div>
        <button className="node-close-btn" onClick={() => deleteNode(id)} title="Eliminar nodo">
          <X size={14} />
        </button>
      </div>

      <div className="captureFrame-body">
        {!hasInput && (
          <p className="captureFrame-empty">Conectá un video para capturar un cuadro.</p>
        )}
        {hasInput && !videoUrl && (
          <p className="captureFrame-empty">El nodo conectado todavía no tiene video.</p>
        )}

        {videoUrl && (
          <>
            <video
              ref={videoRef}
              className="captureFrame-video"
              src={videoUrl}
              crossOrigin="anonymous"
              preload="auto"
              muted
              playsInline
              onLoadedMetadata={(e) => {
                setDuration(e.target.duration || 0);
                setReady(true);
                if (timeSeconds > 0) e.target.currentTime = Math.min(timeSeconds, e.target.duration);
              }}
              data-testid={`capture-frame-video-${id}`}
            />

            <div className="captureFrame-scrub">
              <button className="tl-icon-btn" onClick={() => stepFrame(-1)}
                      title="Cuadro anterior" data-testid={`capture-frame-prev-${id}`}>
                <ChevronLeft size={14} />
              </button>
              <input
                type="range"
                min={0}
                max={Math.max(duration, 1)}
                step={FRAME_STEP}
                value={Math.min(timeSeconds, duration || timeSeconds)}
                onChange={(e) => setTime(e.target.value)}
                className="captureFrame-slider"
                data-testid={`capture-frame-slider-${id}`}
              />
              <button className="tl-icon-btn" onClick={() => stepFrame(1)}
                      title="Cuadro siguiente" data-testid={`capture-frame-next-${id}`}>
                <ChevronRight size={14} />
              </button>
            </div>

            <div className="captureFrame-time">
              <Clock3 size={12} />
              <input
                type="number"
                min={0}
                step={0.01}
                value={timeSeconds}
                onChange={(e) => setTime(e.target.value)}
                className="captureFrame-time-input nodrag nowheel"
                data-testid={`capture-frame-time-${id}`}
              />
              <span className="captureFrame-time-hint">
                s {duration ? `· dura ${fmt(duration)}` : ''}
              </span>
            </div>

            <button
              className="captureFrame-capture-btn"
              onClick={handleCapture}
              disabled={capturing || !ready || !CAPTURE_FRAME_ENABLED}
              data-testid={`capture-frame-btn-${id}`}
            >
              {capturing
                ? <><Loader2 size={14} className="spin" /> Capturando…</>
                : <><Camera size={14} /> Capturar</>}
            </button>
          </>
        )}

        {error && (
          <div className="captureFrame-error" data-testid={`capture-frame-error-${id}`}>
            <AlertCircle size={13} /> {error}
          </div>
        )}

        {data.outputUrl && (
          <div className={`captureFrame-result ${stale ? 'stale' : ''}`}
               data-testid={`capture-frame-result-${id}`}>
            <img src={data.outputUrl} alt={`Cuadro capturado en ${fmt(data.capturedTimeSeconds)}`} />
            <div className="captureFrame-result-meta">
              {data.width}×{data.height} · {fmt(data.capturedTimeSeconds)}
              {stale && <span className="captureFrame-stale-badge">desactualizado</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

CaptureFrameNode.displayName = 'CaptureFrameNode';

export default CaptureFrameNode;
